import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Breadcrumbs,
  Link,
  Typography,
  IconButton,
  Container,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeContext } from '../../contexts/ThemeContext';
import UserProfileMenu from '../../components/UserProfileMenu';
import AuthorChip from '../../components/AuthorChip';
import usePermissions from '../../hooks/usePermissions';
import { SiteVisitLogService } from '../../services/SiteVisitLogService';
import { SiteVisitLog } from '../../models/SiteVisitLog';
import VisitLogDetailDialog from '../ProjectDashboardPage/dialogs/VisitLogDetailDialog';
import VisitLogFormDialog from '../../components/dialogs/VisitLogFormDialog';
import RecordFormDialog from '../../components/dialogs/RecordFormDialog';

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
};

interface Snack {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

const SiteVisitLogPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useContext(ThemeContext);
  const { canEdit } = usePermissions();
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNum = projectId ? Number(projectId) : NaN;

  const stored = localStorage.getItem('selectedProject');
  const projectName = stored ? (JSON.parse(stored).name as string | undefined) : undefined;

  const [logs, setLogs] = useState<SiteVisitLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 등록은 통합 다이얼로그, 수정은 출장 일지 폼을 직접 연다 — 저장된 일지를
  // 다른 종류로 바꿀 수는 없으므로 수정에는 종류 선택기가 없다.
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<SiteVisitLog | null>(null);
  const [snack, setSnack] = useState<Snack>({ open: false, message: '', severity: 'success' });
  const [detailId, setDetailId] = useState<number | null>(null);

  const detailLog = useMemo(
    () => (detailId == null ? null : logs.find((l) => l.id === detailId) ?? null),
    [detailId, logs],
  );

  const refresh = useCallback(async () => {
    if (!Number.isFinite(projectIdNum)) return;
    setLoading(true);
    setError(null);
    try {
      const list = await SiteVisitLogService.list(projectIdNum);
      setLogs(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : '목록 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [projectIdNum]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => setCreateOpen(true);

  const openEdit = (log: SiteVisitLog) => setEditing(log);

  const notifySaved = (message: string, saved: unknown) => {
    setSnack({ open: true, message, severity: 'success' });
    if (saved) refresh();
  };

  const handleDelete = async (log: SiteVisitLog) => {
    if (!window.confirm(`'${log.visited_at.substring(0, 10)} / ${log.visitor_name}' 출장 일지를 삭제하시겠습니까?`)) return;
    try {
      await SiteVisitLogService.delete(log.id);
      setSnack({ open: true, message: '삭제되었습니다', severity: 'success' });
      await refresh();
    } catch (e) {
      setSnack({ open: true, message: e instanceof Error ? e.message : '삭제 실패', severity: 'error' });
    }
  };

  const totalCount = useMemo(() => logs.length, [logs]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 4,
          py: 1.5,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/project/${projectId}`)} size="small" aria-label="뒤로가기">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/projects')}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              현장 목록
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate(`/project/${projectId}`)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {projectName || `프로젝트 ${projectId}`}
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
              <AssignmentIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              출장 일지
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            title={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) },
            }}
          >
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
          <UserProfileMenu />
        </Box>
      </Paper>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              출장 일지 ({totalCount}건)
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="새로고침">
              <span>
                <IconButton onClick={refresh} disabled={loading} aria-label="새로고침">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            {/* 버튼이 곧 종류 — 일지 등록. 종류 선택 단계는 없앴다. */}
            {canEdit && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                일지 등록
              </Button>
            )}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : logs.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">등록된 출장 일지가 없습니다.</Typography>
              {canEdit ? (
                <Typography variant="caption">우측 상단의 "일지 추가" 버튼으로 등록하세요.</Typography>
              ) : (
                <Typography variant="caption">편집자 또는 관리자만 일지를 등록할 수 있습니다.</Typography>
              )}
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>방문일</TableCell>
                    <TableCell>방문자</TableCell>
                    <TableCell>방문 목적</TableCell>
                    <TableCell>작업 내용</TableCell>
                    <TableCell>후속 조치</TableCell>
                    <TableCell>작성자</TableCell>
                    <TableCell align="right">동작</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      hover
                      onClick={() => setDetailId(log.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.visited_at)}</TableCell>
                      <TableCell>{log.visitor_name}</TableCell>
                      <TableCell>{log.purpose}</TableCell>
                      <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.work_summary || '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.next_action || '—'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <AuthorChip name={log.created_by_name} userId={log.created_by_id} label="" dense />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {canEdit ? (
                          <>
                            <Tooltip title="수정">
                              <IconButton size="small" onClick={() => openEdit(log)} aria-label="수정">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="삭제">
                              <IconButton size="small" color="error" onClick={() => handleDelete(log)} aria-label="삭제">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* 출장 일지 전용 — kinds 를 하나로 좁혀 종류 선택기를 숨긴다. */}
      <RecordFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        initialKind="visitlog"
        kinds={['visitlog']}
        projectId={projectIdNum}
        siteName={projectName}
        onSaved={(r) =>
          notifySaved(
            r.kind === 'file'
              ? `파일 ${r.uploadedCount ?? 0}건이 올라갔습니다`
              : r.kind === 'visitlog'
                ? '출장 일지가 등록되었습니다'
                : r.kind === 'memo'
                  ? '메모가 등록되었습니다'
                  : '일정이 등록되었습니다',
            r.visitLog ?? r.schedule ?? r.uploadedCount ?? null,
          )
        }
      />

      {/* 수정 전용 */}
      <VisitLogFormDialog
        open={editing != null}
        onClose={() => setEditing(null)}
        projectId={projectIdNum}
        siteName={projectName}
        editing={editing}
        onSaved={(saved) => {
          setEditing(null);
          notifySaved('출장 일지가 수정되었습니다', saved);
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      <VisitLogDetailDialog
        open={detailId != null}
        log={detailLog}
        onClose={() => setDetailId(null)}
        onEdit={(log) => {
          setDetailId(null);
          openEdit(log);
        }}
        onDelete={async (log) => {
          await handleDelete(log);
          setDetailId(null);
        }}
      />
    </Box>
  );
};

export default SiteVisitLogPage;
